import { useEffect, useState } from 'react';

import { reactIcons as Icon } from '@project44/manifest-ds';

import { EntityType } from '../../definitions/types/Entity';
import { useWorkspaceActions } from '../../providers/WorkspaceActions';
import { scopedT } from '../../utils/helpers/scopedT';
import { Tooltip } from '../Tooltip';

import {
  StyledIconButton,
  StyledLoadingIconButton,
  StyledSpinner,
} from './styles';

interface OpenEntityButtonProps {
  entity: EntityType;
  onAddToWorkspace?: () => void;
  onSelectInWorkspace?: () => void;
}

export const OpenEntityButton = ({
  entity,
  onAddToWorkspace,
  onSelectInWorkspace,
}: OpenEntityButtonProps) => {
  const {
    state: { entities },
    add,
    select,
  } = useWorkspaceActions();

  const t = scopedT();

  const workspaceEntity: EntityType | undefined = entities?.find(
    (e) => e.type === entity.type && e.id === entity.id,
  );

  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tooltipText, setTooltipText] = useState(t('openInWorkspace'));

  useEffect(() => {
    const loaded = !!(workspaceEntity && workspaceEntity.label);
    const loading = !!(workspaceEntity && !workspaceEntity.label);
    setIsLoaded(loaded);
    setIsLoading(loading);
    setTooltipText(
      loaded
        ? t('jumpToEntity', { entityType: entity.type })
        : t('openInWorkspace'),
    );
  }, [entities?.length, workspaceEntity?.label]);

  const handleEntityOpen = () => {
    add(entity);
    onAddToWorkspace?.();
  };

  const handleEntitySelect = () => {
    select(entity, { triggerMethod: 'jump-to-entity-button' });
    onSelectInWorkspace?.();
  };

  if (isLoading) {
    return (
      <StyledLoadingIconButton
        data-testid="open-entity-button-loading"
        variant="secondary"
        size="small"
      >
        <StyledSpinner data-testid="open-entity-button-spinner" />
      </StyledLoadingIconButton>
    );
  }

  return (
    <Tooltip message={tooltipText} data-testid="open-entity-button-tooltip">
      {isLoaded ? (
        <StyledIconButton
          data-testid="open-entity-button-loaded"
          variant="secondary"
          size="small"
          onPress={handleEntitySelect}
        >
          <Icon.JumpToEntity size="small" />
        </StyledIconButton>
      ) : (
        <StyledIconButton
          data-testid="open-entity-button-unloaded"
          variant="secondary"
          size="small"
          onPress={handleEntityOpen}
        >
          <Icon.ChevronRightDouble size="small" />
        </StyledIconButton>
      )}
    </Tooltip>
  );
};
